import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ToggleStatus } from '@unanet/sdk';
import { isReactPage } from '#/lib/page';
import {
  cleanUpIgnoreFlags,
  getPersonStorageStatus,
  parseProps,
  resetFlags,
  setPersonStorageItem,
  toLegacyTimeDashboard,
  toReactTimeDashboard,
} from './utils';
import type { TimeDashboardToggleWebComponentProps } from './models';

export const useTimeDashboardToggle = (
  webComponentProps: TimeDashboardToggleWebComponentProps
) => {
  const { hideAndReset, personKey } = parseProps(webComponentProps);

  const dialogContainerRef = useRef<HTMLDivElement | null>(null);
  const currentPageIsReact = isReactPage();
  const personToggleStatus = useMemo(
    () => getPersonStorageStatus({ personKey }),
    [personKey]
  );

  useEffect(
    function initToggle() {
      const _isReactPage = isReactPage();

      const isLegacyRedirect =
        _isReactPage && personToggleStatus === ToggleStatus.OptedOut;

      const isReactRedirect =
        !_isReactPage && personToggleStatus === ToggleStatus.OptedIn;

      cleanUpIgnoreFlags(_isReactPage);

      if (hideAndReset) {
        resetFlags();
        return;
      }

      if (isLegacyRedirect) {
        //if users have opted-out redirect them back to the legacy dashboard;
        toLegacyTimeDashboard();
        return;
      }

      if (isReactRedirect) {
        //if users have opted-in and redirect them back to the new react dashboard;
        toReactTimeDashboard();
        return;
      }
    },
    [hideAndReset, personKey, personToggleStatus]
  );
  const onOptIn = useCallback(() => {
    if (personKey > -1) {
      setPersonStorageItem({
        personKey,
        toggleStatus: ToggleStatus.OptedIn,
      });

      toReactTimeDashboard();
    }
  }, [personKey]);

  const onOptOut = useCallback(() => {
    if (personKey > -1) {
      setPersonStorageItem({
        personKey,
        toggleStatus: ToggleStatus.OptedOut,
      });

      toLegacyTimeDashboard();
    }
  }, [personKey]);

  return {
    currentPageIsReact,
    dialogContainerRef,
    onOptIn,
    onOptOut,
    personToggleStatus,
  };
};
